
import React from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Warehouse, Package, ArrowRight, Calendar, FileText, Info, Hash, CircleDollarSign } from "lucide-react";
import { TextButton } from "../../../components/ButtonComponents";
import { formatQuantity } from "../../../utils/format";

interface ViewProps {
    isOpen: boolean;
    onClose: () => void;
    data: any; // StockMovement veya GroupedTransfer
}

export const WarehouseMovementViewDialog: React.FC<ViewProps> = ({ isOpen, onClose, data }) => {
    if (!data) return null;

    const items = data.movements || [data];

    // Para birimi formatlayıcı
    const formatCurrency = (value:any) => {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: data.currency || 'TRY' }).format(value);
    };

    return (
        <Dialog 
            visible={isOpen} onHide={onClose} 
            header={
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                        <FileText size={20} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold leading-none">Hareket Detayı</h2>
                        <p className="text-xs mt-1 font-mono text-gray-500">Ref: {data.id} # {data.source}</p>
                    </div>
                </div>
            }
            footer={<div className="flex justify-end gap-2"><TextButton onClick={onClose} className="btn-cancel">Kapat</TextButton></div>}
            className="w-full max-w-4xl"
        >
            <div className="flex flex-col gap-6 p-1">
                {/* Bilgi Kartları */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Lokasyon ve Cari */}
                    <div className="card2 space-y-3 p-4 border rounded-xl">
                        <div className="flex items-center gap-2   border-b pb-2">
                            <Warehouse size={16} /> <span className="font-bold text-xs uppercase">Lokasyon & Cari</span>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div>
                                <span className="text-[10px]   block">İlgili Depo</span>
                                <span className="font-semibold">{data.warehouse?.name}</span>
                            </div>
                            <div>
                                <span className="text-[10px]  block">İlgili Firma/Cari</span>
                                <span className="font-semibold text-blue-600">{data.firm?.name}</span>
                            </div>
                        </div>
                    </div>

                    {/* İşlem Bilgisi */}
                    <div className="card2 space-y-3 p-4 border rounded-xl">
                        <div className="flex items-center gap-2   border-b pb-2">
                            <Calendar size={16} /> <span className="font-bold text-xs uppercase">İşlem Bilgisi</span>
                        </div>
                        <div className="grid grid-cols-1 gap-2">
                            <div>
                                <span className="text-[10px]   block">Tarih</span>
                                <span className="text-sm font-medium">{new Date(data.createdAt).toLocaleString('tr-TR')}</span>
                            </div>
                            <div>
                                <span className="text-[10px]   block">İşlem Türü</span>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                    data.type?.toUpperCase() === 'IN' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                    {data.accountMovementId ? 'FATURALI' : 'STOK FİŞİ'} ({data.type?.toUpperCase() === 'IN' ? 'GİRİŞ' : 'ÇIKIŞ'})
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Finansal Özet */}
                    <div className="card2 space-y-3  ">
                        <div className="flex items-center gap-2   border-b pb-2">
                            <CircleDollarSign size={16} /> <span className="font-bold text-xs uppercase">Mali Bilgiler</span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <div className="flex justify-between">
                                <span className="text-[10px]  ">Vergi Dahil Toplam</span>
                                <span className="font-bold text-lg  ">{formatCurrency(data.totalAmount)}</span>
                            </div>
                            <div className="flex justify-between border-t pt-1">
                                <span className="text-[10px]  ">KDV (%{data.taxRate})</span>
                                <span className="text-xs font-medium">{formatCurrency(data.taxAmount)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Ürün Tablosu */}
                <div className="card2 p-0 overflow-hidden shadow-sm border rounded-xl">
                    <div className="  px-4 py-3 flex items-center gap-2  ">
                        <Package size={18} className="text-blue-500" />
                        <span className="font-bold text-sm">İşlem Gören Ürünler</span>
                    </div>
                    <DataTable value={items} className="p-datatable-sm" responsiveLayout="scroll">
                       
                        <Column header="Ürün Adı" body={(r) => (
                            <div className="flex flex-col">
                                <span className="font-medium text-sm">{r.stock?.name}</span>
                                <span className="text-[9px] text-gray-400">ID: {r.stockId}</span>
                            </div>
                        )} />
                        <Column header="Birim Fiyat" align="right" body={(r) => formatCurrency(r.unitPrice)} />
                        <Column header="Miktar" align="right" body={(r) => {
                            const isOut = r.type?.toUpperCase() === 'OUT' || r.quantity < 0;
                            return (
                            <span className={`font-bold ${isOut ? 'text-red-600' : 'text-green-600'}`}>
                                {isOut ? '-' : '+'}{formatQuantity(Math.abs(r.quantity))} Adet
                            </span>
                            );
                        }} />
                        <Column header="Toplam" align="right" body={(r) => (
                            <span className="font-bold">{formatCurrency(r.totalAmount)}</span>
                        )} />
                    </DataTable>
                </div>
            </div>
        </Dialog>
    );
};